
import Enumerable from "linq";
import { Component, Mixins } from "vue-property-decorator";
import { IExternalMenuItemConfiguration } from "../../../domain/configuration";
import { IImageSpecifiableButtonState } from "../../panes";
import { DomainRegistry } from "../../../domain";
import { Dictionary } from "../../../domain/system";
import { ApplicationLaunchRequest } from "../../../domain/localhost/items";
import { WindowOpener } from "../utilities";
import { ExternalMenuParameterSource } from "../ExternalMenuParameterSource"; // 循環参照対策で個別importしている

@Component
export default class ExternalMenuMixin extends Mixins() {
  createParameterSource(): ExternalMenuParameterSource {
    const source = new ExternalMenuParameterSource();
    source.userId = this.$store.getters.user.id;
    source.userName = this.$store.getters.user.name;
    source.departmentId = this.$store.getters.department ? this.$store.getters.department.id : null;
    source.orderUserId = this.$store.getters.orderUser ? this.$store.getters.orderUser.id : null;
    source.attendingUserId = this.$store.getters.attendingUser ? this.$store.getters.attendingUser.id : null;
    source.clientIpAddress = this.$store.getters.clientIpAddress ? this.$store.getters.clientIpAddress : null;
    source.hostName = this.$store.getters.hostName ? this.$store.getters.hostName : null;
    return source;
  }

  toButtonState(menu: IExternalMenuItemConfiguration, isLoading: boolean, source: ExternalMenuParameterSource): IImageSpecifiableButtonState {
    const isIconImageSpecified = !!menu.iconClass || !!menu.iconImageSource;
    return {
      definition: {
        name: this.replaceReturnCode(menu.title),
        description: menu.description,
        iconClass: isIconImageSpecified ? menu.iconClass : "document-list", // 指定されていないときはとりあえず過去一覧のアイコンを表示する,
        iconImageSource: menu.iconImageSource,
        disableIconImageSource: menu.disableIconImageSource,
      },
      isVisible: menu.isEnable,
      isEnable: this.isAvailable(menu, isLoading, source),
      onClick: () => this.doAction(menu, source),
    };
  }

  private replaceReturnCode(text: string): string {
    if (!text) { return text; }
    return text
      .replace(/\\r/g, "\r")
      .replace(/\\n/g, "\n");
  }

  private isAvailable(menu: IExternalMenuItemConfiguration, isLoading: boolean, source: ExternalMenuParameterSource): boolean {
    if (!menu.isEnable || (!menu.url && !menu.modulePath)) {
      return false;
    }
    const disturbParameters: string[] = [];
    if (isLoading) {
      // ログイン成功時点で揃うパラメータだけであれば起動可能
      // それ以外のパラメータが含まれていたら起動不可
      disturbParameters.push("%PATIENTCODE%");
      disturbParameters.push("%PATIENTID%");
      disturbParameters.push("%DOCUMENTID%");
    }
    if (!source.departmentId) {
      disturbParameters.push("%DEPARTMENTCODE%");
      disturbParameters.push("%DEPARTMENTID%");
    }
    if (!source.patientId) {
      // 患者ID（チェックするほう）が含まれていたら起動不可
      disturbParameters.push("%PATIENTCODE%");
      disturbParameters.push("%PATIENTID%");
    }
    if (!source.documentId) {
      // 文書IDが含まれていたら起動不可
      disturbParameters.push("%DOCUMENTID%");
    }
    if (disturbParameters.length === 0) {
      return true;
    }
    const argumentsText = this.createArgumentsText(menu.parameters);
    return Enumerable.from(disturbParameters)
      .distinct()
      .all(p => argumentsText.indexOf(p) < 0);
  }

  private doAction(menu: IExternalMenuItemConfiguration, source: ExternalMenuParameterSource): void {
    if (menu.url) {
      const parameters = this.replaceValues(menu.parameters, source);
      if (!menu.ignoreAssistant) {
        this.$requestOpenWindow(menu.url, parameters);
      } else {
        WindowOpener.openWindow([
          menu.url,
          Object.keys(parameters).map(k => k + "=" + parameters[k]).join("&"),
        ].filter(q => !!q).join("?"));
      }
      return;
    }
    if (menu.modulePath && !menu.ignoreAssistant) {
      this.launchApplication(menu, source);
      return;
    }
    this.$logger.warn("実行されませんでした。");
  }

  private launchApplication(menu: IExternalMenuItemConfiguration, source: ExternalMenuParameterSource): void {
    const service = DomainRegistry.localhost.getApplicationService();
    const params = new Dictionary<string, string>();
    params.set("action", "launchApplication");
    const request = this.createApplicationLaunchRequest(menu, source);
    service.launchWithKey(null, params, request);
  }

  private createApplicationLaunchRequest(menu: IExternalMenuItemConfiguration, source: ExternalMenuParameterSource): ApplicationLaunchRequest {
    const request = new ApplicationLaunchRequest();
    request.modulePath = menu.modulePath;
    request.argumentsText = this.createArgumentsText(this.replaceValues(menu.parameters, source));
    return request;
  }

  private createArgumentsText(parameters: { [key: string]: string; }): string {
    const texts: string[] = [];
    for (const key of Object.keys(parameters)) {
      const value = parameters[key];
      if (key === "%JOINED%") {
        // ダブルクォーテーションは自分で指定してもらう
        texts.push(value);
      } else {
        // 各項目をダブルクォーテーションで囲う
        texts.push("\"" + key + "\"");
        texts.push("\"" + value + "\"");
      }
    }
    return texts.join(" ");
  }

  private replaceValues(parameters: { [key: string]: string; }, source: ExternalMenuParameterSource): { [key: string]: string; } {
    if (!parameters) { return {}; }
    const result = {} as any;
    for (const key of Object.keys(parameters)) {
      const value = this.replaceValue(parameters[key], source);
      if (!value) {
        // 値が無い項目はスキップする
        continue;
      }
      result[key] = value;
    }
    return result;
  }

  /**
   * リプレースフィールドを値に置換する
   */
  private replaceValue(parameter: string, source: ExternalMenuParameterSource): string {
    if (!parameter) { return null; }
    return parameter
      .replace(/%USERCODE%/g, source.userId ? source.userId : "")
      .replace(/%USERID%/g, source.userId ? source.userId : "")
      .replace(/%USERNAME%/g, source.userName ? source.userName : "")
      .replace(/%DEPARTMENTCODE%/g, source.departmentId ? source.departmentId : "")
      .replace(/%DEPARTMENTID%/g, source.departmentId ? source.departmentId : "")
      .replace(/%ORDERUSERID%/g, source.orderUserId ? source.orderUserId : "")
      .replace(/%ATTENDINGUSERID%/g, source.attendingUserId ? source.attendingUserId : "")
      .replace(/%PATIENTCODE%/g, source.patientId ? source.patientId : "")
      .replace(/%PATIENTID%/g, source.patientId ? source.patientId : "")
      .replace(/%CLIENTIPADDRESS%/g, source.clientIpAddress ? source.clientIpAddress : "")
      .replace(/%HOSTNAME%/g, source.hostName ? source.hostName : "")
      .replace(/%DOCUMENTID%/g, source.documentId ? source.documentId.toString() : "");
  }
}
